"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Bus, RefreshCw } from "lucide-react"
import "leaflet/dist/leaflet.css"
import axiosInstance from "../../utils/axiosInstance"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import axios from "axios";
import { API_ENDPOINTS } from "@/constants/config";
import TRACKING_SERVICE_URL, { getWebSocketUrl } from "@/constants/tracking";
import { getToken } from "@/utils/authStorage";

const directionsControlStyle = `
.maplibregl-ctrl-directions {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  font-family: inherit;
  font-size: 14px;
  padding: 8px;
  z-index: 1001;
}
.maplibregl-ctrl-directions input {
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 4px 8px;
  margin-bottom: 4px;
}
.maplibregl-ctrl-directions button {
  background: #4629eb;
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 4px 10px;
  cursor: pointer;
  margin-left: 4px;
}
.maplibregl-ctrl-directions button:hover {
  background: #2d1a8a;
}
`;

if (typeof document !== 'undefined' && !document.getElementById('directions-control-style')) {
  const style = document.createElement('style');
  style.id = 'directions-control-style';
  style.innerHTML = directionsControlStyle;
  document.head.appendChild(style);
}

const MobileTrackingApp2 = () => {
  const token = getToken();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [error, setError] = useState(null)
  const [wsConnected, setWsConnected] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState("")
  const [loading, setLoading] = useState(true)
  const [schoolSettings, setSchoolSettings] = useState(null)
  const mapRef = useRef(null)
  const busMarkerRef = useRef(null)
  const wsRef = useRef(null)
  const directionsRef = useRef(null)
  const INITIAL_CENTER = [77.08, 28.48]
  const INITIAL_ZOOM = 14
  const routePolylineRef = useRef(null)
  const pathHistoryRef = useRef([])
  const mapTilerKey = import.meta.env.VITE_MAPTILER_API_KEY;
  const [routeStops, setRouteStops] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const busImgRef = useRef(null);
  const prevBusPositionRef = useRef(null);
  const [studentStop, setStudentStop] = useState(null);
  const [studentRouteData, setStudentRouteData] = useState(null);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState("");
  const [vehicleLocationStatus, setVehicleLocationStatus] = useState(null);
  const [schoolLocation, setSchoolLocation] = useState(null);
  const [currentBusLocation, setCurrentBusLocation] = useState(null);
  const [etaData, setEtaData] = useState(null);
  const stopMarkersRef = useRef([]);
  const recordedStopMarkersRef = useRef([]);
  const schoolMarkerRef = useRef(null);
  const currentBusLocationRef = useRef(null); // Store latest location for ETA without triggering re-renders
  const etaIntervalRef = useRef(null); // Track the interval to prevent duplicates
  const lastEtaRequestRef = useRef(0); // Track last request time for throttling
  const isRecorderMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("recorderMode") === "1";
    } catch {
      return false;
    }
  }, []);

  // Check vehicle location sharing status
  const checkVehicleLocationStatus = async (vehicleId) => {
    try {
      const token = localStorage.getItem("token");
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      let vehicle;

      if (currentUser.role === "vehicle" || currentUser.role === "driver") {
        // vehicle role can't access /vehicles/:id — use own profile endpoint instead
        const response = await axios.get(
          API_ENDPOINTS.vehiclesProfile,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "success") {
          vehicle = response.data.data.vehicle;
        }
      } else {
        const response = await axios.get(
          API_ENDPOINTS.vehicle(vehicleId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "success") {
          vehicle = response.data.data.vehicle;
        }
      }

      if (vehicle) {
        setVehicleLocationStatus({
          isLocationSharingActive: vehicle.locationSharingActive || false,
          lastUpdate: vehicle.locationSharingStartTime,
          vehicleNumber: vehicle.vehicleNumber
        });
      }
    } catch (error) {
      setVehicleLocationStatus({
        isLocationSharingActive: false,
        lastUpdate: null,
        vehicleNumber: "Unknown"
      });
    }
  };

  const validateCoordinates = (lng, lat) => {
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      return INITIAL_CENTER;
    }
    return [lng, lat];
  }

  const updateRouteOnMap = (routePoints = []) => {
    if (!mapRef.current || !Array.isArray(routePoints)) return;

    const coordinates = routePoints
      .map((point) => [Number(point?.longitude), Number(point?.latitude)])
      .filter(
        (coord) =>
          Number.isFinite(coord[0]) &&
          Number.isFinite(coord[1]) &&
          Math.abs(coord[0]) <= 180 &&
          Math.abs(coord[1]) <= 90
      );

    if (coordinates.length < 2) return;

    const geojson = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates
      }
    };

    const renderRoute = () => {
      if (!mapRef.current) return;
      const existingSource = mapRef.current.getSource("recorded-route");
      if (existingSource) {
        existingSource.setData(geojson);
        return;
      }

      mapRef.current.addSource("recorded-route", {
        type: "geojson",
        data: geojson
      });

      mapRef.current.addLayer({
        id: "recorded-route-line",
        type: "line",
        source: "recorded-route",
        paint: {
          "line-color": "#007AFF",
          "line-width": 4
        }
      });
    };

    if (mapRef.current.isStyleLoaded()) {
      renderRoute();
    } else {
      mapRef.current.once("load", renderRoute);
    }
  };

  const addStopMarker = (stop) => {
    if (!mapRef.current || !stop) return;
    const lng = Number(stop.longitude);
    const lat = Number(stop.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    const marker = new maplibregl.Marker({ color: "red" })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    recordedStopMarkersRef.current.push(marker);
  };

  const drawRouteWithDirections = (startCoordinates, endCoordinates, stops = []) => {
    if (!mapRef.current || !startCoordinates || !endCoordinates) return;
    drawRouteManually(startCoordinates, endCoordinates, stops);
  };

  const drawRouteManually = async (startCoordinates, endCoordinates, stops = []) => {
    try {
      const token = localStorage.getItem("token");
      const url = `${TRACKING_SERVICE_URL}/api/vehicle-routes/ors-directions`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          coordinates: [startCoordinates, endCoordinates],
          profile: 'driving-car'
        })
      });

      const data = await response.json();

      const addRouteToMap = () => {
        if (mapRef.current.getSource('manual-route')) {
          mapRef.current.removeLayer('manual-route-line');
          mapRef.current.removeSource('manual-route');
        }
        mapRef.current.addSource('manual-route', {
          type: 'geojson',
          data: data.features[0]
        });
      mapRef.current.addLayer({
        id: 'manual-route-line',
        type: 'line',
        source: 'manual-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#ff0000',
          'line-width': 6,
          'line-blur': 0.4,
          'line-opacity': 1,
        },
      });
        addRouteMarkers(startCoordinates, endCoordinates);
      };

      if (mapRef.current.isStyleLoaded()) {
        addRouteToMap();
      } else {
        mapRef.current.once('load', addRouteToMap);
      }
    } catch (error) {
      setError('Failed to calculate route manually. Please try again.');
    }
  };

  const addRouteMarkers = (startCoordinates, endCoordinates) => {
    if (mapRef.current.getSource('route-markers')) {
      mapRef.current.removeLayer('route-markers');
      mapRef.current.removeSource('route-markers');
    }

    mapRef.current.addSource('route-markers', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: startCoordinates
            },
            properties: {
              title: 'Start Point',
              color: '#00ff00'
            }
          },
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: endCoordinates
            },
            properties: {
              title: 'School',
              color: '#ff0000'
            }
          }
        ]
      }
    });

    mapRef.current.addLayer({
      id: 'route-markers',
      type: 'circle',
      source: 'route-markers',
      paint: {
        'circle-radius': 8,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2
      }
    });
  };

  // Move this function above the useEffect that uses it
  const updateBusLocation = (busLocation) => {
    if (!mapRef.current || !busLocation.location) return
    const coordinates = busLocation.location.coordinates
    // coordinates: [lng, lat]
    const validPosition = validateCoordinates(coordinates[0], coordinates[1]) // lng, lat
    
    // Store the current position before updating
    prevBusPositionRef.current = validPosition;
    
    // Update current bus location state for ETA calculation
    const location = {
      lat: validPosition[1],
      lng: validPosition[0]
    };
    setCurrentBusLocation(location);
    currentBusLocationRef.current = location; // Update ref for ETA calculation
    
    if (busMarkerRef.current) {
      busMarkerRef.current.remove()
    }
    
    // Create modern bus icon with SVG
    const busIconElement = document.createElement("div");
    busIconElement.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#shadow)">
          <!-- Bus body -->
          <rect x="8" y="14" width="32" height="22" rx="4" fill="#FFB300" stroke="#fff" strokeWidth="2"/>
          <!-- Windows -->
          <rect x="11" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
          <rect x="26" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
          <!-- Wheels -->
          <circle cx="16" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
          <circle cx="32" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
          <!-- Headlights -->
          <circle cx="12" cy="32" r="1.5" fill="#FFF176"/>
          <circle cx="36" cy="32" r="1.5" fill="#FFF176"/>
          <!-- Roof line -->
          <line x1="11" y1="14" x2="37" y2="14" stroke="#fff" strokeWidth="1.5" opacity="0.7"/>
        </g>
        <defs>
          <filter id="shadow" x="-2" y="-2" width="52" height="52">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
        </defs>
      </svg>
    `;
    busIconElement.style.display = "flex";
    busIconElement.style.alignItems = "center";
    busIconElement.style.justifyContent = "center";
    busIconElement.style.width = "48px";
    busIconElement.style.height = "48px";
    busIconElement.style.transform = "translate(-24px, -24px)"; // Center the marker
    
    busMarkerRef.current = new maplibregl.Marker({ element: busIconElement })
      .setLngLat(validPosition)
      .addTo(mapRef.current)
    
    // Store reference for zoom scaling
    busImgRef.current = busIconElement.querySelector('svg');
    // Always zoom and follow the bus
    mapRef.current.flyTo({
      center: validPosition,
      zoom: 16,
      speed: 0.8,
      essential: true
    });
    prevBusPositionRef.current = validPosition;
  }

  useEffect(() => {
    const onMessage = (event) => {
      try {
        const rawData = event?.data;
        const data = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        if (!data || typeof data !== "object") return;

        if (data.type === "ROUTE_UPDATE") {
          updateRouteOnMap(data.route || []);
        }

        if (data.type === "ADD_STOP") {
          addStopMarker(data.stop);
        }

        if (data.type === "ROUTE_FINAL") {
          updateRouteOnMap(data.route || []);
          if (Array.isArray(data.stops)) {
            data.stops.forEach((stop) => addStopMarker(stop));
          }
        }
      } catch (e) {
        console.error("Invalid message", e);
      }
    };

    window.addEventListener("message", onMessage);
    document.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("message", onMessage);
    };
  }, []);

  useEffect(() => {
    if (isRecorderMode) {
      setLoading(false);
      return;
    }
    const fetchVehicles = async () => {
      try {
        if (user && user.role === "student" && user._id) {
          // Fetch only the student's assigned vehicle
          const res = await axiosInstance.get(
            API_ENDPOINTS.vehicleRouteStudent(user._id),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.status === "success" && res.data.data.vehicleRoute && res.data.data.vehicleRoute.vehicle) {
            setVehicles([res.data.data.vehicleRoute.vehicle]);
            setSelectedVehicle(res.data.data.vehicleRoute.vehicle._id);
            setStudentRouteData(res.data.data.vehicleRoute);
            
            // Find the student's assigned stop
            const studentId = user._id;
            let foundStop = null;
            
            if (res.data.data.vehicleRoute.shifts && res.data.data.vehicleRoute.shifts.length > 0) {
              for (const shift of res.data.data.vehicleRoute.shifts) {
                for (const stop of shift.stops) {
                  if (stop.students && stop.students.some(s => 
                    (typeof s === 'string' ? s : s._id) === studentId
                  )) {
                    foundStop = {
                      ...stop,
                      shiftType: shift.type,
                      shiftName: shift.name
                    };
                    break;
                  }
                }
                if (foundStop) break;
              }
            }
            
            setStudentStop(foundStop);
          } else {
            setVehicles([]);
            setSelectedVehicle("");
            setError("No vehicle assigned to you.");
          }
        } else if (user && (user.role === "vehicle" || user.role === "driver")) {
          // Fetch only the vehicle driver's own vehicle
          const res = await axiosInstance.get(
            API_ENDPOINTS.vehiclesProfile,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.status === "success" && res.data.data && res.data.data.vehicle) {
            const vehicle = res.data.data.vehicle;
            setVehicles([vehicle]);
            setSelectedVehicle(vehicle._id);
          } else {
            setVehicles([]);
            setSelectedVehicle("");
            setError("No vehicle assigned to you.");
          }
        } else {
          // Default: fetch all vehicles (for admin/teacher roles)
          const response = await axiosInstance.get(API_ENDPOINTS.vehicles, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (response.data.status === "success") {
            setVehicles(response.data.data.vehicles)
            if (!selectedVehicle && response.data.data.vehicles.length > 0) {
              setSelectedVehicle(response.data.data.vehicles[0]._id)
            }
          } else {
            setError("No vehicles available")
          }
        }
      } catch (err) {
        setError(`Failed to load vehicles: ${err.response?.data?.message || err.message || "Unknown error"}`)
      } finally {
        setLoading(false)
      }
    }
    if (token) {
      fetchVehicles()
    } else {
      setError("Authentication token not found")
      setLoading(false)
    }
  }, [isRecorderMode, token, selectedVehicle])

  useEffect(() => {
    if (!isRecorderMode || mapRef.current) return;

    const mapContainer = document.getElementById("map");
    if (!mapContainer) return;

    mapRef.current = new maplibregl.Map({
      container: "map",
      style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM
    });

    mapRef.current.on("load", () => {
      mapRef.current?.addControl(new maplibregl.NavigationControl(), "top-right");
    });

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {
          // Silent fail
        }
        mapRef.current = null;
      }
    };
  }, [isRecorderMode, mapTilerKey]);

  useEffect(() => {
    if (isRecorderMode) return;
    if (!selectedVehicle) {
      return
    }

    // Only initialize map if vehicle is online and location sharing is active
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) {
      // Clean up existing map if vehicle goes offline
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      return
    }

    if (!mapRef.current) {
      // Check if map container exists before initializing
      const mapContainer = document.getElementById("map");
      if (!mapContainer) {
        return;
      }

      mapRef.current = new maplibregl.Map({
        container: "map",
        style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
        center: prevBusPositionRef.current || INITIAL_CENTER,
        zoom: prevBusPositionRef.current ? 16 : INITIAL_ZOOM,
      })
      // Add zoom event listener for bus icon scaling
      mapRef.current.on('zoom', () => {
        if (busImgRef.current) {
          const zoom = mapRef.current.getZoom();
          // Scale between 32px and 64px depending on zoom
          const size = Math.max(32, Math.min(64, 32 + (zoom - 12) * 3));
          busImgRef.current.setAttribute('width', size);
          busImgRef.current.setAttribute('height', size);
        }
      });
      
      mapRef.current.on('load', () => {
        mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right")
        
        if (selectedVehicle) {
          axios.get(`${TRACKING_SERVICE_URL}/api/vehicle-locations?vehicleId=${selectedVehicle}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
            .then(res => {
              if (res.data.status === 'success' && res.data.data && res.data.data.busLocations && res.data.data.busLocations.length > 0) {
                updateBusLocation(res.data.data.busLocations[0]);
              }
            })
            .catch(() => {
              // Silently fail - not found is fine, just means no location yet
            });
        }
      })
    }

    if (busMarkerRef.current) {
      busMarkerRef.current.remove()
      busMarkerRef.current = null
    }

    const connectWebSocket = () => {
      // Guard: without a token the server will reject the upgrade with 401
      if (!token) {
        console.warn("[MobileTrackingApp2] WebSocket: no auth token found, live tracking disabled.");
        return;
      }

      // Clean up existing WebSocket connection first
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, "Switching vehicles");
        }
        wsRef.current = null;
      }

      // If we have a previous position, keep the map centered there during reconnection
      if (prevBusPositionRef.current && mapRef.current) {
        mapRef.current.setCenter(prevBusPositionRef.current);
      }

      // Small delay to ensure old connection is closed
      setTimeout(() => {
        const ws = new WebSocket(getWebSocketUrl(token))

        let reconnectAttempts = 0;
        const maxReconnectAttempts = 5;
        const reconnectDelay = 30000;
        // Tracks whether onopen ever fired; used to detect upgrade-level auth rejection
        let wasOpened = false;

        wsRef.current = ws

        ws.onopen = () => {
          wasOpened = true;
          setWsConnected(true)
          setError(null)
          reconnectAttempts = 0;

          ws.send(JSON.stringify({
            type: "subscribe",
            vehicleId: selectedVehicle,
          }));
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === "locationUpdate" && data.busLocation) {
              if (data.busLocation.vehicle._id === selectedVehicle) {
                updateBusLocation(data.busLocation)
              }
            }
          } catch (error) {
            setError("Error processing WebSocket data. Attempting to reconnect...");
            setWsConnected(false)
          }
        }

        ws.onerror = () => {
          setError("Connection error occurred. Attempting to reconnect...");
          setWsConnected(false)
        }

        ws.onclose = (event) => {
          setWsConnected(false)

          // Upgrade rejected before connection opened — most likely an auth failure.
          // Stop reconnecting to avoid token-spam.
          if (!wasOpened && event.code === 1006) {
            console.warn("[MobileTrackingApp2] WebSocket upgrade rejected — token may be expired. Please refresh.");
            setError("Tracking connection rejected. Please refresh the page.");
            return;
          }

          if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            setTimeout(connectWebSocket, reconnectDelay)
          } else if (reconnectAttempts >= maxReconnectAttempts) {
            setError("Maximum reconnection attempts reached. Please refresh the page.");
          }
        }
      }, 100); // 100ms delay
    }

    connectWebSocket()

    return () => {
      if (busMarkerRef.current) {
        busMarkerRef.current.remove()
        busMarkerRef.current = null
      }
      // Clean up stop markers
      stopMarkersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          // Silent fail
        }
      });
      stopMarkersRef.current = [];
      
      // Clean up school marker
      if (schoolMarkerRef.current) {
        try {
          schoolMarkerRef.current.remove();
        } catch (e) {
          // Silent fail
        }
        schoolMarkerRef.current = null;
      }

      recordedStopMarkersRef.current.forEach((marker) => {
        try {
          marker.remove();
        } catch (e) {
          // Silent fail
        }
      });
      recordedStopMarkersRef.current = [];
      
      if (mapRef.current) {
        if (mapRef.current.getSource && mapRef.current.getSource('route')) {
          try {
            mapRef.current.removeLayer('route-line');
            mapRef.current.removeSource('route');
          } catch (e) {}
        }
        if (mapRef.current.getSource && mapRef.current.getSource('manual-route')) {
          try {
            mapRef.current.removeLayer('manual-route-line');
            mapRef.current.removeSource('manual-route');
          } catch (e) {}
        }
        if (mapRef.current.getSource && mapRef.current.getSource('route-markers')) {
          try {
            mapRef.current.removeLayer('route-markers');
            mapRef.current.removeSource('route-markers');
          } catch (e) {}
        }
        if (mapRef.current.getSource && mapRef.current.getSource("recorded-route")) {
          try {
            if (mapRef.current.getLayer("recorded-route-line")) {
              mapRef.current.removeLayer("recorded-route-line");
            }
            mapRef.current.removeSource("recorded-route");
          } catch (e) {}
        }
        mapRef.current.remove()
        mapRef.current = null
      }
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "unsubscribe",
              busRouteId: selectedVehicle
            })
          )
        }
        
        wsRef.current.close(1000, "Component unmounting")
        wsRef.current = null
      }
      
      routePolylineRef.current = null
      pathHistoryRef.current = []
    }
  }, [isRecorderMode, token, selectedVehicle, vehicleLocationStatus])

  // Route polyline effect
  useEffect(() => {
    if (!mapRef.current || !routeCoordinates || routeCoordinates.length === 0) return;
    
    // Don't add route if vehicle is offline
    if (!isRecorderMode && vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) return;

    const addRoute = () => {
      if (routePolylineRef.current && mapRef.current.getSource('route')) {
        try {
          mapRef.current.removeLayer('route-line');
          mapRef.current.removeSource('route');
        } catch (e) {}
        routePolylineRef.current = null;
      }

      // const lats = routeCoordinates.map(coord => coord[1]);
      // const lngs = routeCoordinates.map(coord => coord[0]);
      // const bounds = [
      //   [Math.min(...lngs), Math.min(...lats)],
      //   [Math.max(...lngs), Math.max(...lats)]
      // ];
      // mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 17 });

      mapRef.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: routeCoordinates,
          },
        },
      });
      mapRef.current.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#4629eb',
          'line-width': 5,
          'line-blur': 0.4,
          'line-opacity': 1,
        },
      });
      routePolylineRef.current = true;
    };

    if (mapRef.current.isStyleLoaded()) {
      addRoute();
    } else {
      mapRef.current.once('load', addRoute);
    }
  }, [isRecorderMode, routeCoordinates, vehicleLocationStatus]);

  useEffect(() => {
    if (isRecorderMode) return;
    if (selectedVehicle) {
      // Check vehicle location sharing status first
      checkVehicleLocationStatus(selectedVehicle);
      
      // Fetch last known location from tracking-service directly
      axios.get(`${TRACKING_SERVICE_URL}/api/vehicle-locations?vehicleId=${selectedVehicle}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(res => {
          if (res.data.status === 'success' && res.data.data && res.data.data.busLocations && res.data.data.busLocations.length > 0) {
            updateBusLocation(res.data.data.busLocations[0]);
          }
        })
        .catch(err => {
          // Not found is fine, just means no location yet
          if (err.response?.status !== 404) {
            // Silently fail - not found is fine
          }
        });
    }
  }, [isRecorderMode, selectedVehicle]);

  useEffect(() => {
    // Get schoolId from user in localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user && user.school) setSchoolId(user.school);
  }, []);

  // Fetch stops for selected vehicle (direct axios, no service)
  useEffect(() => {
    const fetchRoute = async () => {
      if (!selectedVehicle) return;

      const token = localStorage.getItem("token");
      let route = null;

      // Student role: vehicleRoutesBySchool is admin-only — use already-fetched studentRouteData
      if (user && user.role === "student" && studentRouteData) {
        route = studentRouteData;
      } else if (user && (user.role === "vehicle" || user.role === "driver")) {
        // Vehicle users should fetch their shifts directly
        try {
          const res = await axios.get(
            API_ENDPOINTS.getShiftsByVehicleId(selectedVehicle || ""),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.status === 'success' && res.data.data) {
            route = {
              shifts: res.data.data.shifts || [],
              vehicle: { _id: selectedVehicle }
            };
          } else {
            setRouteStops([]);
            setRouteCoordinates([]);
            return;
          }
        } catch (err) {
          setRouteStops([]);
          setRouteCoordinates([]);
          return;
        }
      } else {
        if (!schoolId) return;
        try {
          const res = await axios.get(
            API_ENDPOINTS.vehicleRoutesBySchool(schoolId),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const routes = res.data.data.vehicleRoutes || [];
          route = routes.find(r => r.vehicle && r.vehicle._id === selectedVehicle);
        } catch (err) {
          setRouteStops([]);
          setRouteCoordinates([]);
          return;
        }
      }

      if (route && route.shifts && route.shifts.length > 0) {
        setAvailableShifts(route.shifts);

        if (!selectedShift && route.shifts.length > 0) {
          setSelectedShift(route.shifts[0]._id);
        }

        const currentShift = selectedShift ?
          route.shifts.find(s => s._id === selectedShift) :
          route.shifts[0];

        const stops = currentShift ? currentShift.stops || [] : [];
        setRouteStops(stops);
        let coords = stops.map(stop => [
          parseFloat(stop.longitude),
          parseFloat(stop.latitude)
        ]);
        // Fetch school location separately (for marker, not for polyline)
        try {
          const schoolRes = await axios.get(
            API_ENDPOINTS.schoolAbout,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const schoolLoc = schoolRes.data.data.school.location;
          if (schoolLoc && schoolLoc.latitude && schoolLoc.longitude) {
            setSchoolLocation({
              longitude: parseFloat(schoolLoc.longitude),
              latitude: parseFloat(schoolLoc.latitude)
            });
          } else {
            setSchoolLocation(null);
          }
        } catch (schoolErr) {
          setSchoolLocation(null);
        }
        // ORS road-following polyline
        if (coords.length >= 2) {
          try {
            const orsRes = await axios.post(
              `${TRACKING_SERVICE_URL}/api/vehicle-routes/ors-directions`,
              { coordinates: coords, profile: 'driving-car' },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (orsRes.data && orsRes.data.features && orsRes.data.features.length > 0) {
              setRouteCoordinates(orsRes.data.features[0].geometry.coordinates);
            } else {
              setRouteCoordinates(coords);
            }
          } catch (orsErr) {
            setRouteCoordinates(coords);
          }
        } else {
          setRouteCoordinates(coords);
        }
      } else {
        setRouteStops([]);
        setRouteCoordinates([]);
      }
    };
    fetchRoute();
  }, [selectedVehicle, schoolId, selectedShift, studentRouteData]);

  // Stops and school marker effect
  useEffect(() => {
    if (!mapRef.current || !routeStops.length) {
      return;
    }
    
    // Don't add stops if vehicle is offline
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) return;

    const addStopsAndSchool = async () => {
      // Remove old stop markers
      stopMarkersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          // Silent fail
        }
      });
      stopMarkersRef.current = [];
      
      // Remove old school marker
      if (schoolMarkerRef.current) {
        try {
          schoolMarkerRef.current.remove();
        } catch (e) {
          // Silent fail
        }
        schoolMarkerRef.current = null;
      }
      
      // Add markers for each stop with numbers
      routeStops.forEach((stop, index) => {
        const lat = parseFloat(stop.latitude);
        const lng = parseFloat(stop.longitude);
        const isCheckpoint = stop.isCheckpoint || false;
        const isStudentStop = studentStop && studentStop._id === stop._id;
        
        // Determine size and color based on position and checkpoint status
        const markerSize = isCheckpoint ? 22 : 32;
        const fontSize = isCheckpoint ? 10 : 12;
        const borderWidth = isCheckpoint ? 2 : 3;
        
        let backgroundColor;
        if (isCheckpoint) {
          backgroundColor = '#ffa500'; // Orange for checkpoints
        } else if (isStudentStop) {
          backgroundColor = '#ff6b35'; // Red-orange for student's stop
        } else if (index === 0) {
          backgroundColor = '#00bcd4'; // Cyan for first stop
        } else if (index === routeStops.length - 1) {
          backgroundColor = '#ff6b35'; // Red-orange for last stop
        } else {
          backgroundColor = '#4629eb'; // Blue for regular stops
        }
        
        // Create stop marker element
        const el = document.createElement('div');
        el.className = 'stop-marker';
        el.innerHTML = `
          <div style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            background: ${backgroundColor};
            border: ${borderWidth}px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: white;
            font-size: ${fontSize}px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            cursor: pointer;
          ">
            ${index + 1}
          </div>
        `;
        
        let popupContent = `<strong>${stop.name}</strong><br/>Stop ${index + 1}`;
        if (isCheckpoint) {
          popupContent += `<br/><span style="color: #ffa500; font-weight: bold;">📍 Checkpoint</span>`;
        }
        if (isStudentStop && studentStop) {
          popupContent = `
            <div style="text-align: center;">
              <div style="color: #ff6b35; font-weight: bold; font-size: 16px; margin-bottom: 5px;">My Stop</div>
              <div style="font-weight: bold; margin-bottom: 3px;">${stop.name}</div>
              <div style="font-size: 12px; color: #666;">
                Stop ${index + 1} • ${studentStop.shiftType === 'pickup' ? 'Pickup' : 'Drop'} Stop
              </div>
            </div>
          `;
        }
        
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([lng, lat])
          .setPopup(
            new maplibregl.Popup({ offset: 25 })
              .setHTML(popupContent)
          )
          .addTo(mapRef.current);
        
        stopMarkersRef.current.push(marker);
      });
      
      // Add school marker at school location
      if (schoolLocation) {
        const schoolCoord = [schoolLocation.longitude, schoolLocation.latitude];
        
        // Create school marker element
        const schoolEl = document.createElement('div');
        schoolEl.className = 'school-marker';
        schoolEl.innerHTML = `
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#school-shadow)">
              <path d="M20 8 L6 18 L34 18 Z" fill="#4629eb" stroke="#fff" stroke-width="2"/>
              <rect x="8" y="18" width="24" height="16" rx="2" fill="#5B6FE8"/>
              <rect x="16" y="24" width="8" height="10" rx="1" fill="#fff"/>
              <circle cx="22" cy="29" r="0.8" fill="#4629eb"/>
              <rect x="10" y="21" width="4" height="4" rx="0.5" fill="#fff" opacity="0.9"/>
              <rect x="26" y="21" width="4" height="4" rx="0.5" fill="#fff" opacity="0.9"/>
              <rect x="19.5" y="6" width="1" height="8" fill="#fff"/>
              <path d="M20.5 6 L20.5 8 L24 7 Z" fill="#FF5252"/>
            </g>
            <defs>
              <filter id="school-shadow" x="-2" y="-2" width="44" height="44">
                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
          </svg>
        `;
        schoolEl.style.cursor = 'pointer';
        
        schoolMarkerRef.current = new maplibregl.Marker({ element: schoolEl })
          .setLngLat(schoolCoord)
          .setPopup(
            new maplibregl.Popup({ offset: 25 })
              .setHTML('<strong>School</strong>')
          )
          .addTo(mapRef.current);
      }
    };

    if (mapRef.current.isStyleLoaded()) {
      addStopsAndSchool();
    } else {
      mapRef.current.once('load', addStopsAndSchool);
    }
  }, [routeStops, routeCoordinates, studentStop, vehicleLocationStatus, schoolLocation]);

  const handleVehicleChange = (vehicleId) => {
    setSelectedVehicle(vehicleId)
    setSelectedShift("") // Reset shift when vehicle changes
    setWsConnected(false) // Reset WebSocket connection state
    setError(null) // Clear any previous errors
    setVehicleLocationStatus(null) // Reset vehicle status
  }

  const handleShiftChange = (shiftId) => {
    setSelectedShift(shiftId)
  }

  // Fetch school settings
  useEffect(() => {
    const fetchSchoolSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        if (!token || !userStr) return;
        
        const user = JSON.parse(userStr);
        const res = await axios.get(API_ENDPOINTS.schoolAbout, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSchoolSettings(res.data.data.settings);
      } catch (error) {
        // Silently fail - school settings are optional
      }
    };
    fetchSchoolSettings();
  }, []);

  // Periodic check for vehicle location status
  useEffect(() => {
    if (!selectedVehicle) return;

    const checkStatusInterval = setInterval(() => {
      checkVehicleLocationStatus(selectedVehicle);
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkStatusInterval);
  }, [selectedVehicle]);

  // ETA calculation effect - only re-run when vehicle/stop/user changes, not on every location update
  useEffect(() => {
    // Clear any existing interval first
    if (etaIntervalRef.current) {
      clearInterval(etaIntervalRef.current);
      etaIntervalRef.current = null;
    }

    if (!user || user.role !== "student" || !selectedVehicle || !studentStop) {
      setEtaData(null);
      lastEtaRequestRef.current = 0; // Reset throttling
      return;
    }

    // Get token from localStorage inside the function to avoid dependency issues
    const getToken = () => localStorage.getItem("token");
    
    // Store current values to avoid stale closures
    const currentVehicleId = selectedVehicle;
    const currentStop = studentStop;
    const currentStopLat = parseFloat(currentStop.latitude);
    const currentStopLng = parseFloat(currentStop.longitude);

    // Calculate ETA for students - defined inside effect to avoid stale closures
    // Added throttling to prevent too many requests
    const fetchETA = async () => {
      const now = Date.now();
      const MIN_REQUEST_INTERVAL = 10000; // Minimum 10 seconds between requests
      
      // Throttle: don't make request if last one was less than 10 seconds ago
      if (now - lastEtaRequestRef.current < MIN_REQUEST_INTERVAL) {
        return;
      }

      if (!currentBusLocationRef.current || !currentVehicleId || !currentStop) {
        return;
      }

      lastEtaRequestRef.current = now;

      try {
        const token = getToken();
        const response = await axios.post(
          API_ENDPOINTS.vehicleRoutesCalculateEta,
          {
            vehicleId: currentVehicleId,
            targetLat: currentStopLat,
            targetLng: currentStopLng,
            targetType: 'stop'
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.status === 'success') {
          setEtaData(response.data.data);
        }
      } catch (error) {
        setEtaData(null);
      }
    };

    // Initial fetch only if we have a location (with throttling check)
    if (currentBusLocationRef.current) {
      fetchETA();
    }
    
    // Set up interval to fetch ETA every 10 seconds
    // This uses the ref, so it always gets the latest location without re-running the effect
    etaIntervalRef.current = setInterval(() => {
      if (currentBusLocationRef.current) {
        fetchETA();
      }
    }, 10000);

    return () => {
      if (etaIntervalRef.current) {
        clearInterval(etaIntervalRef.current);
        etaIntervalRef.current = null;
      }
    };
  }, [selectedVehicle, studentStop?._id, user?.role, user?._id]); // Use stop ID instead of whole object

  // If bus service is disabled, show a message
  if (schoolSettings && !schoolSettings.enableBusService?.enabled) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Bus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Bus Service Disabled</h3>
          <p className="text-gray-500">Bus tracking is currently not available for this school.</p>
        </div>
      </div>
    );
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
        <p>Loading vehicles...</p>
      </div>
    )
  }

  // Show loading state while checking vehicle status
  if (selectedVehicle && vehicleLocationStatus === null) {
    return (
      <div className="flex items-center justify-center p-6">
        <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
        <p>Checking vehicle status...</p>
      </div>
    )
  }

  return (
    <Card className="w-full shadow-xl rounded-2xl border-0 bg-gradient-to-br from-blue-50 to-white">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-400 rounded-t-2xl p-3 flex flex-row items-center justify-end gap-2">
        {vehicleLocationStatus && vehicleLocationStatus.isLocationSharingActive && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-blue-700">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            Vehicle Online
          </span>
        )}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
          ${wsConnected ? "bg-white/90 text-green-700" : "bg-white/90 text-red-700"}`}
        >
          <span className={`w-2 h-2 rounded-full mr-2 ${wsConnected ? "bg-green-500" : "bg-red-500"}`}></span>
          {wsConnected ? "Connected" : "Reconnecting..."}
        </span>
      </CardHeader>
      <CardContent className="p-6">
        {error ? (
          <Alert variant="destructive" className="mb-6 rounded-lg shadow bg-red-50 border-red-200">
            <AlertDescription className="font-medium text-red-800">{error}</AlertDescription>
          </Alert>
        ) : (!isRecorderMode && vehicles.length === 0) ? (
          <div className="p-8 text-center text-gray-500">
            <p>No vehicles available. Please create a vehicle first.</p>
          </div>
        ) : (
          <div className="space-y-6 relative">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
              <div className="flex-1">
                <Select value={selectedVehicle} onValueChange={handleVehicleChange} disabled={user && user.role === "student"}>
                  <SelectTrigger className="w-full h-12 text-lg border-blue-200 shadow-sm rounded-lg bg-white focus:border-blue-400">
                    <SelectValue placeholder="Select a vehicle" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] rounded-lg shadow-lg border-blue-100">
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle._id} value={vehicle._id} className="text-base">
                        <span className="font-semibold text-blue-700">{vehicle.vehicleNumber}</span>
                        <span className="ml-2 text-xs text-gray-500">({vehicle.type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {availableShifts.length > 1 && (
                <div className="flex-1">
                  <Select value={selectedShift} onValueChange={handleShiftChange}>
                    <SelectTrigger className="w-full h-12 text-lg border-blue-200 shadow-sm rounded-lg bg-white focus:border-blue-400">
                      <SelectValue placeholder="Select a shift" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] rounded-lg shadow-lg border-blue-100">
                      {availableShifts.map((shift) => (
                        <SelectItem key={shift._id} value={shift._id} className="text-base">
                          <span className="font-semibold text-blue-700">{shift.name}</span>
                          <span className="ml-2 text-xs text-gray-500">({shift.type})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {/* ETA Display for Students */}
            {user && user.role === "student" && etaData && vehicleLocationStatus?.isLocationSharingActive && (
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 font-medium">
                      Estimated arrival at your stop
                    </p>
                    <p className="text-2xl font-bold text-blue-700">
                      {etaData.estimatedTimeFormatted}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {etaData.distanceKm} km away via route
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Show map or offline message based on vehicle status */}
            {vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive ? (
              <div className="mt-2 relative z-10 rounded-xl overflow-hidden shadow-lg border border-blue-100 bg-gray-50">
                <div className="h-[500px] w-full rounded-xl border-0 flex items-center justify-center">
                  <div className="text-center">
                    <Bus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">Vehicle Offline</h3>
                    <p className="text-gray-500 mb-4">
                      {vehicleLocationStatus.vehicleNumber} is not currently sharing location.
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      Location Sharing Inactive
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 relative z-10 rounded-xl overflow-hidden shadow-lg border border-blue-100">
                <div
                  id="map"
                  className="h-[500px] w-full rounded-xl border-0"
                  style={{ position: "relative", zIndex: 10 }}
                ></div>
              </div>
            )}
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-6 mt-6 justify-center md:justify-start p-4 bg-white rounded-lg shadow-sm border border-blue-100">
              <div className="flex items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="8" y="14" width="32" height="22" rx="4" fill="#FFB300" stroke="#fff" strokeWidth="2"/>
                  <rect x="11" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
                  <rect x="26" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
                  <circle cx="16" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
                  <circle cx="32" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
                </svg>
                <span className="text-sm font-medium text-gray-700">Bus</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#00bcd4',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-sm font-medium text-gray-700">First Stop</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#4629eb',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-sm font-medium text-gray-700">Stop</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#ff6b35',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-sm font-medium text-gray-700">Last Stop{user && user.role === "student" && studentStop ? ' / My Stop' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{
                  width: '18px',
                  height: '18px',
                  background: '#ffa500',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-sm font-medium text-gray-700">Checkpoint</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 8 L6 18 L34 18 Z" fill="#4629eb" stroke="#fff" strokeWidth="2"/>
                  <rect x="8" y="18" width="24" height="16" rx="2" fill="#5B6FE8"/>
                  <rect x="16" y="24" width="8" height="10" rx="1" fill="#fff"/>
                  <rect x="10" y="21" width="4" height="4" rx="0.5" fill="#fff" opacity="0.9"/>
                  <rect x="26" y="21" width="4" height="4" rx="0.5" fill="#fff" opacity="0.9"/>
                </svg>
                <span className="text-sm font-medium text-gray-700">School</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default MobileTrackingApp2